import { app, authentication } from "@microsoft/teams-js";
import {
  selectCurrentUserAccountId,
  selectGetUserStatus,
  signUserIn,
} from "app/containers/Global/slice";
import { Skeleton } from "app/shadcnComponents/ui/skeleton";
import { useEffect, useRef, useState } from "react";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import { setUserToken } from "utils/helperFunctions";
import jwtDecode from "jwt-decode";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import App from "./App";
import type { InitialRoute } from "./modules/common";

const LOADING_QUOTES = [
  "Great leaders don't set out to be leaders — they set out to make a difference.",
  "The strength of the team is each individual member. The strength of each member is the team.",
  "Coming together is a beginning, staying together is progress, and working together is success.",
  "Leadership is not about being in charge. It's about taking care of those in your charge.",
  "Alone we can do so little; together we can do so much.",
  "The best way to predict the future is to create it.",
  "People don't care how much you know until they know how much you care.",
  "Success is best when it's shared.",
  "A team is not a group of people who work together. It is a group of people who trust each other.",
  "What you do has far greater impact than what you say.",
];

type TeamsTabStatus = "loading" | "sso_failed" | "authenticated" | "error";

// Deep links (e.g. Teams bot messages) pass `context={"subEntityId": ...}`, surfaced as page.subPageId.
// Whitelisted so arbitrary deep-link values can't drive navigation.
const SUB_PAGE_ROUTES: Record<string, string> = {
  checkIn: "/user/checkIn",
  dashboard: "/user/dashboard",
};

const ONE_ON_ONE_PREP_SUB_PAGE = /^oneOnOnePrep:(\d+)$/;

function resolveSubPage(subPageId: string): InitialRoute | undefined {
  if (SUB_PAGE_ROUTES[subPageId]) {
    return { path: SUB_PAGE_ROUTES[subPageId] };
  }

  const oneOnOnePrep = ONE_ON_ONE_PREP_SUB_PAGE.exec(subPageId);
  if (oneOnOnePrep) {
    return {
      path: "/user/team",
      params: { oneOnOnePrepUserAccountId: oneOnOnePrep[1] },
    };
  }

  return undefined;
}

const SSO_TIMEOUT_MS = 5000;
const LOADING_TIMEOUT_MS = 20000;

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms
    );
    promise.then(
      (val) => {
        clearTimeout(timer);
        resolve(val);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

function TeamsLoadingScreen() {
  const [quoteIndex, setQuoteIndex] = useState(() =>
    Math.floor(Math.random() * LOADING_QUOTES.length) // NOSONAR: non-security use, cycling loading screen quotes
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setQuoteIndex((prev) => (prev + 1) % LOADING_QUOTES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-screen gap-6 px-8">
      <Skeleton className="h-10 w-10 rounded-full" />
      <p className="text-sm text-gray-500 text-center max-w-md italic">
        &ldquo;{LOADING_QUOTES[quoteIndex]}&rdquo;
      </p>
    </div>
  );
}

export default function TeamsTab() {
  const dispatch = useAppDispatch();
  const currentUserAccountId = useAppSelector(selectCurrentUserAccountId);
  const getUserStatus = useAppSelector(selectGetUserStatus);
  const authAttempted = useRef(false);

  const [status, setStatus] = useState<TeamsTabStatus>(
    currentUserAccountId === null ? "loading" : "authenticated"
  );
  const [errorMessage, setErrorMessage] = useState<string>("");
  // Only read once at load: Teams can keep returning the same subPageId for the session,
  // and we don't want to drag the user back to that view after they navigate away.
  const [initialRoute, setInitialRoute] = useState<InitialRoute | undefined>();
  // App.tsx signs the user in from a stored token in parallel, so the user can be ready before
  // Teams returns the context; the router only reads its route on mount, so wait for the context.
  const [isInitialRouteResolved, setIsInitialRouteResolved] = useState(
    currentUserAccountId !== null
  );

  // When user data loads successfully, show the app
  useEffect(() => {
    if (currentUserAccountId !== null) {
      setStatus("authenticated");
    }
  }, [currentUserAccountId]);

  // If getUser failed, show error
  useEffect(() => {
    if (getUserStatus === "failed" && currentUserAccountId === null) {
      setStatus("error");
      setErrorMessage(
        "Failed to load user data. Please refresh and try again."
      );
    }
  }, [getUserStatus, currentUserAccountId]);

  // Safety timeout — if still loading after 20s, show error
  useEffect(() => {
    if (status !== "loading") return;
    const timer = setTimeout(() => {
      setStatus("error");
      setErrorMessage(
        "Sign-in timed out. Please check your connection and refresh."
      );
    }, LOADING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [status]);

  // One-time auth attempt on mount
  useEffect(() => {
    if (currentUserAccountId !== null || authAttempted.current) return;
    authAttempted.current = true;

    let cancelled = false;

    const authenticateViaTeamsSSO = async () => {
      // Local dev bypass — set REACT_APP_TEAMS_DEV_TOKEN in .env.development.
      // Only honored when running the local Vite dev server (NODE_ENV=development),
      // so it can never activate in a built/deployed bundle even if the env var leaks in.
      const devToken = process.env.REACT_APP_TEAMS_DEV_TOKEN;
      // if (devToken && process.env.NODE_ENV === "development") {
      if (devToken) {
        const decoded = jwtDecode<DecodedJWT>(devToken);
        setUserToken(devToken);
        dispatch(signUserIn({ userAccountId: decoded.sub }));
        setIsInitialRouteResolved(true);
        return;
      }

      await withTimeout(
        app.initialize(),
        SSO_TIMEOUT_MS,
        "Teams app.initialize()"
      );

      try {
        const context = await withTimeout(
          app.getContext(),
          SSO_TIMEOUT_MS,
          "Teams app.getContext()"
        );
        const subPageId = context.page?.subPageId;
        if (subPageId) {
          setInitialRoute(resolveSubPage(subPageId));
        }
      } catch (contextError) {
        // Not fatal — fall back to the default dashboard view
        console.warn("[TeamsTab] Failed to read Teams context:", contextError);
      } finally {
        setIsInitialRouteResolved(true);
      }

      const ssoToken = await withTimeout(
        authentication.getAuthToken(),
        SSO_TIMEOUT_MS,
        "Teams getAuthToken()"
      );

      const response = await fetch(
        `${REACT_APP_API_URL}/talentInsights/teams/sso-exchange`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ssoToken }),
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const detail = errorBody.detail ? ` — ${errorBody.detail}` : "";
        throw new Error(
          `${errorBody.error ?? "SSO exchange failed"} (${
            response.status
          })${detail}`
        );
      }

      const data = await response.json();

      // Always set token and sign in — don't check `cancelled` here because
      // React StrictMode in dev double-mounts effects, cancelling the first run.
      // The SSO exchange already succeeded so we must complete the sign-in.
      setUserToken(data.token);
      dispatch(signUserIn({ userAccountId: data.userAccountId }));
      try {
        app.notifySuccess();
      } catch {
        // notifySuccess can fail if already notified
      }
    };

    const initialize = async () => {
      try {
        await authenticateViaTeamsSSO();
        return;
      } catch (ssoError: any) {
        const reason = ssoError?.message ?? String(ssoError);
        console.warn("[TeamsTab] SSO failed:", reason, ssoError);
        if (cancelled) return;
        setErrorMessage(reason);
        setStatus("error");
        try {
          app.notifyFailure({
            reason: app.FailedReason.Other,
            message: reason,
          });
        } catch {
          // SDK not initialized
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "loading") {
    return <TeamsLoadingScreen />;
  }

  if (status === "sso_failed") {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 px-6 text-center">
        <p className="text-sm text-gray-600">
          Teams SSO not available. Trying alternative sign-in...
        </p>
        <p className="text-xs text-gray-400">{errorMessage}</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 px-6 text-center">
        <p className="text-lg font-semibold">Unable to sign in</p>
        <p className="text-sm text-gray-600">{errorMessage}</p>
      </div>
    );
  }

  if (!isInitialRouteResolved) {
    return <TeamsLoadingScreen />;
  }

  return <App initialRoute={initialRoute} />;
}
